import Konva from 'konva'
import StageData from '@ps/store/workbench/stage/stage-data'
import { rulerOptions } from '@ps/store/workbench/stage/config/ruler-config'
import { rulerParams } from '@ps/types/workbench/stage.type'
import {
  createReferenceLine, getAdjacentPosition,
  moveReferenceLineByPosition,
  reDrawReferenceLine,
  removeAllReferenceLine
} from '@ps/store/workbench/stage/reference-line'
import store from '@ps/store'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { EditorDataScript } from '@ps/bridge/qc/scripts/editor'
import gameIframe from '@ps/bridge/iframe/iframe'
import { LAYOUT_RESOLUTION } from '@ps/bridge/qc/scripts/layout'

// 描绘标尺，缩放/旋转屏幕和初始化的时候都会调用
export function drawRuler({ space, startCoord, step }: rulerParams): void {
  if (!StageData.rulerLayer || !StageData.stage) {
    return
  }
  removeRuler()
  addHorizontalRuler({ space, startCoord, step })
  addVerticalRuler({ space, startCoord, step })
  addRulerEvent()
  const drawReferenceLine = () => {
    const gamePlay = gameIframe.gamePlay
    const adapterResolution: LAYOUT_RESOLUTION = store.state.workspace.game.device.adapterResolution
    if (gamePlay) {
      const ped = editorScript.getScript(gamePlay) as EditorDataScript
      if (ped.$data.referenceLine) {
        const referenceLineSetData = ped.$data.referenceLine[adapterResolution]
        reDrawReferenceLine(referenceLineSetData)
      }
    }
  }
  drawReferenceLine()
}

/**
 * 添加水平标尺
 * @param space 刻度间隔
 * @param startCoord 刻度起始坐标
 * @param step 刻度显示步长，多少个刻度显示一个数字
 */
function addHorizontalRuler({ space, startCoord, step }: rulerParams): void {
  const horizontalGroup = new Konva.Group({ id: 'horizontalGroup' })
  StageData.rulerLayer?.add(horizontalGroup)
  const horizontalLines: any[] = []
  const options = rulerOptions
  const maxWidth = StageData.stage?.width()
  const rulerSpace = space || options.space
  let index = 0
  if (maxWidth) {
    const addLines = (coord: number, index: number): void => {
      let points = [coord, 0, coord, options.lineH] // 普通刻度
      if (index % step === 0) {
        // 满 step 刻度
        points = [coord, 0, coord, options.lineH * 2]
        // 刻度文字
        const text = new Konva.Text({
          x: coord,
          y: options.lineH * 2,
          text: String(index * 10),
          fontSize: options.textSize,
          fill: options.textColor
        })
        horizontalLines.push(text)
      }
      horizontalLines.push(
        new Konva.Line({
          points,
          stroke: options.lineColor,
          strokeWidth: options.lineW
        })
      )
    }
    // 从开始坐标往右描绘
    for (let i = startCoord.x; i < maxWidth; i += rulerSpace) {
      addLines(i, index)
      index = index + 1
    }
    index = 0
    for (let i = startCoord.x; i > 0; i -= rulerSpace) {
      // 避免重复画 0 刻度
      if (i !== startCoord.x) {
        addLines(i, index)
      }
      index = index - 1
    }

    // 横向背景色
    const horizontalBg = new Konva.Rect({
      x: 0,
      y: 0,
      width: maxWidth,
      height: options.wrapSize,
      fill: options.wrapBg
    })
    horizontalGroup.add(horizontalBg, ...horizontalLines)
  }
}

/**
 * 添加水平标尺
 * @param space 刻度间隔
 * @param startCoord 刻度起始坐标
 * @param step 刻度显示步长，多少个刻度显示一个数字
 */
function addVerticalRuler({ space, startCoord, step }: rulerParams): void {
  const verticalGroup = new Konva.Group({ id: 'verticalGroup' })
  StageData.rulerLayer?.add(verticalGroup)
  const verticalLines: any[] = []
  const options = rulerOptions
  const maxHeight = StageData.stage?.height()
  const rulerSpace = space || options.space
  let index = 0
  if (maxHeight) {
    const addLines = (coord: number, index: number): void => {
      let points = [0, coord, options.lineH, coord] // 普通刻度
      if (index % step === 0) {
        // 满 step 刻度
        points = [0, coord, options.lineH * 2, coord]
        // 刻度文字
        const text = new Konva.Text({
          x: options.lineH * 2,
          y: coord,
          text: String(index * 10),
          fontSize: options.textSize,
          fill: options.textColor
        })
        verticalLines.push(text)
      }
      verticalLines.push(
        new Konva.Line({
          points,
          stroke: options.lineColor,
          strokeWidth: options.lineW
        })
      )
    }
    // 从开始坐标往右描绘
    for (let i = startCoord.y; i < maxHeight; i += rulerSpace) {
      addLines(i, index)
      index = index + 1
    }
    index = 0
    for (let i = startCoord.y; i > 0; i -= rulerSpace) {
      // 避免重复画 0 刻度
      if (i !== startCoord.y) {
        addLines(i, index)
      }
      index = index - 1
    }

    // 纵向背景色
    const verticalBg = new Konva.Rect({
      x: 0,
      y: 0,
      width: options.wrapSize,
      height: maxHeight,
      fill: options.wrapBg
    })
    verticalGroup.add(verticalBg, ...verticalLines)
  }
}

export function removeRuler(): void {
  const idArr = ['horizontalGroup', 'verticalGroup']
  idArr.forEach(id => {
    const group = StageData.rulerLayer?.findOne(`#${id}`)
    if (group) {
      group.off('.ruler')
      group.destroy()
    }
  })
  removeAllReferenceLine()
}

function addRulerEvent(): void {
  const horizontalGroup = StageData.rulerLayer?.findOne('#horizontalGroup')
  const verticalGroup = StageData.rulerLayer?.findOne('#verticalGroup')
  // 新增临时辅助线，用户 mouseover 中显示
  let horizontalTempLine: Konva.Group | null = null
  let verticalTempLine: Konva.Group | null = null
  const Stage = StageData.stage!
  if (horizontalGroup) {
    horizontalGroup
      .on('mousemove.ruler', () => {
        if (verticalTempLine) {
          moveReferenceLineByPosition({
            lineGroup: verticalTempLine,
            pointerPosition: Stage.getPointerPosition()!,
            type: 'vertical'
          })
        }
      })
      .on('mouseenter.ruler', () => {
        if (!verticalTempLine) {
          verticalTempLine = createReferenceLine({
            id: 'verticalTempLine',
            type: 'vertical',
            pointerPosition: Stage.getPointerPosition()!,
            isTemp: true
          }) as Konva.Group
        } else {
          verticalTempLine.visible(true)
        }
      })
      .on('mouseleave.ruler', () => {
        if (verticalTempLine) {
          verticalTempLine.visible(false)
        }
      })
      .on('click.ruler', () => {
        const adjacentPosition = getAdjacentPosition({
          pointerPosition: Stage.getPointerPosition()!
        })
        createReferenceLine({
          type: 'vertical',
          pointerPosition: adjacentPosition
        })
      })
  }
  if (verticalGroup) {
    verticalGroup
      .on('mousemove.ruler', () => {
        if (horizontalTempLine) {
          moveReferenceLineByPosition({
            lineGroup: horizontalTempLine,
            pointerPosition: Stage.getPointerPosition()!,
            type: 'horizontal'
          })
        }
      })
      .on('mouseenter.ruler', () => {
        if (!horizontalTempLine) {
          horizontalTempLine = createReferenceLine({
            id: 'horizontalTempLine',
            type: 'horizontal',
            pointerPosition: Stage.getPointerPosition()!,
            isTemp: true
          }) as Konva.Group
        } else {
          horizontalTempLine.visible(true)
        }
      })
      .on('mouseleave.ruler', () => {
        if (horizontalTempLine) {
          horizontalTempLine.visible(false)
        }
      })
      .on('click.ruler', () => {
        const adjacentPosition = getAdjacentPosition({
          pointerPosition: Stage.getPointerPosition()!
        })
        createReferenceLine({
          type: 'horizontal',
          pointerPosition: adjacentPosition
        })
      })
  }
}
